/**
 * Autonomy Policy Dry-Run Simulator — "what would this policy have done?"
 *
 * Admins editing autonomy thresholds are otherwise flying blind: raising an
 * auto-approve ceiling or flipping a rule to 'block' changes how EVERY future
 * proposal is routed, with no way to see the blast radius before hitting
 * Save. This module replays a candidate policy over the last N real
 * proposals and reports, deterministically, how each one WOULD have been
 * routed — and, for a candidate vs the current policy, exactly which
 * proposals change buckets.
 *
 * Honesty rules:
 *   • Classification is delegated to the SHIPPED threshold engine
 *     ({@link evaluateAction} from autonomyPolicy.ts) — never forked or
 *     re-implemented, so the dry-run can never drift from the real gate.
 *   • Pure and IO-free: proposals are passed in as data; callers own reads.
 *   • A dry-run never mutates anything — it is a pure projection.
 */

import { evaluateAction, type AutonomyPolicy, type PolicyMode } from './autonomyPolicy';

// Re-exported so consumers (and tests) of the simulator can import the
// policy shape from this module alone.
export type { AutonomyPolicy, PolicyMode, PolicyRule } from './autonomyPolicy';
export { emptyPolicy, upsertRule } from './autonomyPolicy';

/** How many recent proposals a preview replays by default. */
export const SIM_WINDOW = 50;

/**
 * A historical proposal, reduced to the three fields the policy engine
 * routes on. Adapt real ApprovalItem rows to this shape at the call site.
 */
export type ScoredProposal = {
  id: string;
  /** Proposing agent id (or source, e.g. 'scheduler'). */
  agentId: string;
  /** Action type the proposal carries (e.g. 'create_task'). */
  actionType: string;
  /** $ impact the proposal touches; 0 when it carries none. */
  impact: number;
};

/** Where a proposal lands: auto-approved, human inbox, or blocked. */
export type SimOutcome = 'auto' | 'human' | 'block';

/** Bucket counts across a simulated window. */
export type SimCounts = {
  autoApprove: number;
  needsHuman: number;
  block: number;
};

/** Full dry-run result: counts plus the per-proposal routing. */
export type PolicySimResult = SimCounts & {
  items: { id: string; outcome: SimOutcome }[];
};

/** A proposal whose routing differs between current and candidate policy. */
export type PolicySimMove = { id: string; from: SimOutcome; to: SimOutcome };

/** What a candidate policy WOULD change vs the current one. */
export type PolicySimDiff = {
  moved: PolicySimMove[];
  /** candidate − current, per bucket. Sums to zero across the three. */
  deltas: SimCounts;
};

/** Map the engine's PolicyMode to the simulator's outcome vocabulary. */
function toOutcome(mode: PolicyMode): SimOutcome {
  return mode === 'ask' ? 'human' : mode;
}

/**
 * Route one historical proposal through a policy using the shipped engine.
 * Exported so UI rows can badge individual proposals consistently.
 */
export function classifyProposal(
  policy: AutonomyPolicy,
  proposal: ScoredProposal,
): SimOutcome {
  return toOutcome(
    evaluateAction(policy, {
      agentId: proposal.agentId,
      actionType: proposal.actionType,
      impact: proposal.impact,
    }),
  );
}

/**
 * Replay `policy` over `recentProposals`. The three bucket counts always
 * sum to `recentProposals.length`; an empty window is honestly all zeros.
 */
export function simulatePolicy(
  policy: AutonomyPolicy,
  recentProposals: ScoredProposal[],
): PolicySimResult {
  const counts: SimCounts = { autoApprove: 0, needsHuman: 0, block: 0 };
  const items: { id: string; outcome: SimOutcome }[] = [];
  for (const p of recentProposals) {
    const outcome = classifyProposal(policy, p);
    if (outcome === 'auto') counts.autoApprove += 1;
    else if (outcome === 'human') counts.needsHuman += 1;
    else counts.block += 1;
    items.push({ id: p.id, outcome });
  }
  return { ...counts, items };
}

/**
 * Diff two policies over the same window: which proposals change buckets
 * (`moved`, with from/to), and the net bucket deltas (candidate − current).
 * Both sides are classified by {@link simulatePolicy}, i.e. by the one true
 * engine — there is no second threshold implementation to disagree with.
 */
export function diffPolicySim(
  current: AutonomyPolicy,
  candidate: AutonomyPolicy,
  recentProposals: ScoredProposal[],
): PolicySimDiff {
  const before = simulatePolicy(current, recentProposals);
  const after = simulatePolicy(candidate, recentProposals);

  const moved: PolicySimMove[] = [];
  for (let i = 0; i < recentProposals.length; i++) {
    const from = before.items[i].outcome;
    const to = after.items[i].outcome;
    if (from !== to) moved.push({ id: recentProposals[i].id, from, to });
  }

  return {
    moved,
    deltas: {
      autoApprove: after.autoApprove - before.autoApprove,
      needsHuman: after.needsHuman - before.needsHuman,
      block: after.block - before.block,
    },
  };
}
